import {
    getPaymentMethod,
    getPriceCountry,
    PaymentMethodId,
    paymentMethods,
    PriceCountry,
    priceCountries,
    standardProductCodesIn,
    toPaymentMethodId,
} from '../../constants/merchantPriceCatalog';
import {
    MerchantMethodPrices,
    MerchantPriceList,
    MerchantPriceMarket,
    MerchantPriceProduct,
} from '../../types/onboarding/MerchantPrices';
import { SalesSegment } from '../../types/onboarding/OnboardStore';

/** The fees priced per payment method, in column order. */
export type FeeField =
    | 'serviceFee'
    | 'transactionFee'
    | 'recurringServiceFee'
    | 'recurringTransactionFee';

export const feeFields: FeeField[] = [
    'serviceFee',
    'transactionFee',
    'recurringServiceFee',
    'recurringTransactionFee',
];

/** Fields that are percentages (0–100); the rest are amounts. */
const percentFields: FeeField[] = ['serviceFee', 'recurringServiceFee'];

export const salesSegments: SalesSegment[] = ['B2C', 'B2B'];

/** The MAX length of an Edge product code, as the API validates it. */
export const MAX_PRODUCT_CODE_LENGTH = 16;

/** The fees of one payment method in one country and segment, as typed in the grid (empty = not priced). */
export interface PriceCell {
    serviceFee: string;
    transactionFee: string;
    recurringServiceFee: string;
    recurringTransactionFee: string;
}

/**
 * Which purchase fees are charged on refunds too, for the whole list: when checked, every priced method gets its own
 * service fee or transaction fee as its refund fee on save.
 */
export interface RefundChoices {
    serviceFee: boolean;
    transactionFee: boolean;
}

export type RefundChoice = keyof RefundChoices;

/** One product code of a payment method in one market and segment. */
export interface ProductRow {
    productCode: string;
    productType: string;
    /** Start of the code's validity, for methods with validity dates (campaigns). */
    validFrom?: string;
    /** End of the code's validity, for methods with validity dates (campaigns). */
    validUntil?: string;
}

/**
 * The product rows per payment method, market and segment, keyed by <see cref="cellKey"/>. Codes are listed where they
 * are priced: a code added in one market does not show up in another.
 */
export type ProductRows = Record<string, ProductRow[]>;

export type ValidityField = 'validFrom' | 'validUntil';

/**
 * The price grid as react-hook-form holds it: per method, market and segment whether the method is active, its typed fees
 * and its product codes (a field array each, carrying the campaign validity of the codes), and for the whole list which
 * fees are charged on refunds. Records are keyed by <see cref="cellKey"/>.
 */
export interface PriceFormValues {
    active: Record<string, boolean>;
    fees: Record<string, PriceCell>;
    products: ProductRows;
    refunds: RefundChoices;
}

/** The field key of one payment method in one country and segment. Keys never contain dots, so they stay flat paths. */
export const cellKey = (
    paymentMethod: PaymentMethodId,
    countryCode: string,
    segment: SalesSegment
) => `${paymentMethod}|${countryCode}|${segment}`;

export const emptyCell = (): PriceCell => ({
    serviceFee: '',
    transactionFee: '',
    recurringServiceFee: '',
    recurringTransactionFee: '',
});

const normalizeNumber = (value: string) => (value ?? '').trim().replace(',', '.');

/** Whether a typed value is a number; blank counts as valid (not priced). */
export const isNumeric = (value: string): boolean => {
    const text = normalizeNumber(value);

    return text === '' || !Number.isNaN(Number(text));
};

/** The typed fee as a number, or undefined when blank or not a number. */
export const parseFee = (value: string): number | undefined => {
    const text = normalizeNumber(value);

    if (text === '') return undefined;

    const number = Number(text);

    return Number.isNaN(number) ? undefined : number;
};

/** A fee from the API as the grid shows it: blank when not priced. */
export const formatFee = (value: number | undefined | null): string =>
    value === undefined || value === null ? '' : String(value);

export const sameCode = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The rows of one method in one market and segment; none when nothing is priced there. */
export const productRowsOf = (products: ProductRows, key: string): ProductRow[] =>
    products[key] ?? [];

/** Every row of one market and segment, whatever the method: a code can only be priced once there. */
export const productRowsOfSegment = (
    products: ProductRows,
    countryCode: string,
    segment: SalesSegment
): ProductRow[] =>
    paymentMethods.flatMap(method =>
        productRowsOf(products, cellKey(method.id, countryCode, segment))
    );

/** Rows by code. */
export const sortProductRows = (rows: ProductRow[]): ProductRow[] =>
    [...rows].sort((a, b) => a.productCode.localeCompare(b.productCode));

const sameProductRow = (a: ProductRow, b: ProductRow) =>
    a.productCode === b.productCode &&
    a.productType === b.productType &&
    (a.validFrom || '') === (b.validFrom || '') &&
    (a.validUntil || '') === (b.validUntil || '');

/** The state of one method in one market and segment as the grid holds it. */
export interface PriceCellState {
    active: boolean;
    fees: PriceCell;
    products: ProductRow[];
}

/**
 * Whether a method in one market and segment differs from what was loaded: switched on or off, a fee typed or cleared,
 * or a code added, removed or redated. Marks the row as unsaved in the grid.
 */
export const isCellChanged = (
    defaults: PriceFormValues,
    key: string,
    current: PriceCellState
): boolean => {
    const loadedFees = defaults.fees[key] ?? emptyCell();
    const loadedRows = productRowsOf(defaults.products, key);

    return (
        current.active !== (defaults.active[key] ?? false) ||
        feeFields.some(field => (current.fees?.[field] ?? '') !== (loadedFees[field] ?? '')) ||
        current.products.length !== loadedRows.length ||
        current.products.some((row, index) => !sameProductRow(row, loadedRows[index]))
    );
};

/**
 * The product rows of the regular setup for every method, market and segment: the starting point for a merchant without
 * prices. Nothing is activated; the codes only make the methods possible to price.
 */
export const standardProductRows = (countries: PriceCountry[]): ProductRows =>
    Object.fromEntries(
        paymentMethods.flatMap(method =>
            countries.flatMap(country =>
                salesSegments.map((segment): [string, ProductRow[]] => [
                    cellKey(method.id, country.code, segment),
                    method.segments.includes(segment) &&
                    country.segments.includes(segment) &&
                    country.groups.includes(method.group)
                        ? sortProductRows(standardProductCodesIn(method, country.code, segment))
                        : [],
                ])
            )
        )
    );

/**
 * The form values with the regular setup filled in. A standard code is added to its method in a market and segment unless
 * the code is already priced there, under any method, and every method that received a code is switched on, since the
 * regular setup is meant to be priced. What is there stays as it is, so a second fill changes nothing.
 */
export const withStandardProducts = (
    values: PriceFormValues,
    countries: PriceCountry[]
): PriceFormValues => {
    const standard = standardProductRows(countries);

    const filledCells = countries.flatMap(country =>
        salesSegments.flatMap(segment => {
            const priced = productRowsOfSegment(values.products, country.code, segment);
            const isMissing = (row: ProductRow) =>
                !priced.some(existing => sameCode(existing.productCode, row.productCode));

            return paymentMethods
                .map(method => cellKey(method.id, country.code, segment))
                .map(key => ({ key, missing: productRowsOf(standard, key).filter(isMissing) }))
                .filter(({ missing }) => missing.length > 0)
                .map(({ key, missing }) => ({
                    key,
                    rows: sortProductRows([...productRowsOf(values.products, key), ...missing]),
                }));
        })
    );

    return {
        ...values,
        products: {
            ...values.products,
            ...Object.fromEntries(filledCells.map(({ key, rows }) => [key, rows])),
        },
        active: {
            ...values.active,
            ...Object.fromEntries(filledCells.map(({ key }) => [key, true])),
        },
    };
};

/** Why a product code cannot be added next to the given rows, or undefined when it can. */
export const validateProductCode = (
    rows: ProductRow[],
    productCode: string
): string | undefined => {
    const code = (productCode ?? '').trim();

    if (code.length === 0) return 'Enter a product code';

    if (code.length > MAX_PRODUCT_CODE_LENGTH)
        return `Product code can be at most ${MAX_PRODUCT_CODE_LENGTH} characters`;

    if (rows.some(product => sameCode(product.productCode, code)))
        return 'The product code is already priced in this market and segment';

    return undefined;
};

/** The countries of the appendix plus any other priced country, and the form values the grid starts from. */
export interface PriceListDefaults {
    countries: PriceCountry[];
    values: PriceFormValues;
}

/**
 * The segments the grid shows for a country: those the appendix offers there, plus any the merchant already has codes in,
 * so stored prices are never hidden.
 */
export const segmentsShownFor = (country: PriceCountry, products: ProductRows): SalesSegment[] =>
    salesSegments.filter(
        segment =>
            country.segments.includes(segment) ||
            productRowsOfSegment(products, country.code, segment).length > 0
    );

const withoutUndefined = <T extends object>(values: T): T =>
    Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined)) as T;

const hasOtherFee = (values: MerchantMethodPrices) =>
    values.transactionFee !== undefined ||
    values.recurringServiceFee !== undefined ||
    values.recurringTransactionFee !== undefined;

const toCell = (values: MerchantMethodPrices): PriceCell => ({
    // A method active without fees is stored with service fee 0 by the API; show it as unpriced again.
    serviceFee: values.serviceFee === 0 && !hasOtherFee(values) ? '' : formatFee(values.serviceFee),
    transactionFee: formatFee(values.transactionFee),
    recurringServiceFee: formatFee(values.recurringServiceFee),
    recurringTransactionFee: formatFee(values.recurringTransactionFee),
});

/** A validity timestamp from the API as the date input of the code holds it. */
const toDateValue = (value: string | undefined) => (value ? value.slice(0, 10) : undefined);

const toRows = (products: MerchantPriceProduct[] | undefined): ProductRow[] =>
    sortProductRows(
        (products ?? []).map(product =>
            withoutUndefined({
                productCode: product.productCode.trim(),
                productType: product.productType,
                validFrom: toDateValue(product.validFrom),
                validUntil: toDateValue(product.validUntil),
            })
        )
    );

/** The countries of the appendix plus any other country the list prices, in that order. */
const toCountries = (markets: MerchantPriceMarket[]): PriceCountry[] => {
    const known = priceCountries.map(country => country.code);
    const extra = markets
        .map(market => market.countryCode?.trim().toUpperCase())
        .filter(code => code && !known.includes(code))
        .sort();

    return [...priceCountries, ...Array.from(new Set(extra)).map(getPriceCountry)];
};

const marketCode = (market: MerchantPriceMarket) => market.countryCode?.trim().toUpperCase();

export const toPriceListDefaults = (list: MerchantPriceList | undefined): PriceListDefaults => {
    const markets = list?.markets ?? [];
    const countries = toCountries(markets);

    const loaded = markets.flatMap(market =>
        salesSegments.flatMap(segment =>
            (market.segments?.[segment] ?? []).map(methodPrices => ({
                key: cellKey(
                    toPaymentMethodId(methodPrices.paymentMethod),
                    marketCode(market),
                    segment
                ),
                cell: toCell(methodPrices),
                rows: toRows(methodPrices.products),
                prices: methodPrices,
            }))
        )
    );

    // Every field the grid can show gets a default, so react-hook-form has something to reset to.
    const allKeys = paymentMethods.flatMap(method =>
        countries.flatMap(country =>
            salesSegments.map(segment => cellKey(method.id, country.code, segment))
        )
    );

    const values: PriceFormValues = {
        active: Object.fromEntries([
            ...allKeys.map((key): [string, boolean] => [key, false]),
            ...loaded.map((entry): [string, boolean] => [entry.key, true]),
        ]),
        fees: Object.fromEntries([
            ...allKeys.map((key): [string, PriceCell] => [key, emptyCell()]),
            ...loaded.map((entry): [string, PriceCell] => [entry.key, entry.cell]),
        ]),
        products: Object.fromEntries([
            ...allKeys.map((key): [string, ProductRow[]] => [key, []]),
            ...loaded.map((entry): [string, ProductRow[]] => [entry.key, entry.rows]),
        ]),
        // A refund fee anywhere in the list means that fee is charged on refunds; the checkboxes cover the whole list.
        refunds: {
            serviceFee: loaded.some(entry => entry.prices.refundServiceFee !== undefined),
            transactionFee: loaded.some(entry => entry.prices.refundTransactionFee !== undefined),
        },
    };

    return { countries, values };
};

/**
 * The form values turned back into the list the API takes: per market with at least one active method, per segment, the
 * method fees with the codes priced there. A fee chosen to be charged on refunds is sent as the method's refund fee too.
 * Recurring fees are only sent for methods with recurring purchases, and validity dates only for methods that carry them.
 */
export const toMerchantPriceList = (
    values: PriceFormValues,
    countries: PriceCountry[]
): MerchantPriceList => {
    const toProducts = (rows: ProductRow[], hasValidityDates: boolean): MerchantPriceProduct[] =>
        rows.map(row =>
            withoutUndefined({
                productCode: row.productCode,
                productType: row.productType,
                validFrom: hasValidityDates ? row.validFrom || undefined : undefined,
                validUntil: hasValidityDates ? row.validUntil || undefined : undefined,
            })
        );

    /** The fee entries of the active methods of one country and segment, in appendix order. */
    const toMethodPrices = (country: PriceCountry, segment: SalesSegment): MerchantMethodPrices[] =>
        paymentMethods
            .map(method => ({
                method,
                key: cellKey(method.id, country.code, segment),
                rows: productRowsOf(values.products, cellKey(method.id, country.code, segment)),
            }))
            // The API rejects a priced method without product codes, so a code-less method is never sent.
            .filter(({ key, rows }) => values.active[key] && rows.length > 0)
            .map(({ method, key, rows }) => {
                const cell = values.fees[key] ?? emptyCell();
                const serviceFee = parseFee(cell.serviceFee);
                const transactionFee = parseFee(cell.transactionFee);

                return withoutUndefined<MerchantMethodPrices>({
                    paymentMethod: method.id,
                    serviceFee,
                    transactionFee,
                    refundServiceFee: values.refunds.serviceFee ? serviceFee : undefined,
                    refundTransactionFee: values.refunds.transactionFee
                        ? transactionFee
                        : undefined,
                    recurringServiceFee: method.supportsRecurring
                        ? parseFee(cell.recurringServiceFee)
                        : undefined,
                    recurringTransactionFee: method.supportsRecurring
                        ? parseFee(cell.recurringTransactionFee)
                        : undefined,
                    products: toProducts(
                        rows,
                        getPaymentMethod(method.id).hasValidityDates ?? false
                    ),
                });
            });

    const markets: MerchantPriceMarket[] = countries
        .map(country => ({
            country,
            segments: Object.fromEntries(
                salesSegments
                    .map(segment => [segment, toMethodPrices(country, segment)] as const)
                    .filter(([, methods]) => methods.length > 0)
            ) as Partial<Record<SalesSegment, MerchantMethodPrices[]>>,
        }))
        .filter(({ segments }) => Object.keys(segments).length > 0)
        .map(({ country, segments }) => ({
            countryCode: country.code,
            currency: country.currency,
            segments,
        }));

    return { markets };
};

const fieldLabels: Record<FeeField, string> = {
    serviceFee: 'Service fee',
    transactionFee: 'Transaction fee',
    recurringServiceFee: 'Recurring service fee',
    recurringTransactionFee: 'Recurring transaction fee',
};

/** The problem with a typed fee, or undefined when it is fine; blank is fine (not priced). */
export const validateFee = (field: FeeField, value: string): string | undefined => {
    if (!isNumeric(value)) return `${fieldLabels[field]} must be a number`;

    const number = parseFee(value);

    if (number === undefined) return undefined;

    if (percentFields.includes(field) && (number < 0 || number > 100))
        return `${fieldLabels[field]} must be between 0 and 100 %`;

    if (number < 0) return `${fieldLabels[field]} cannot be negative`;

    return undefined;
};

/** The react-hook-form validation rule of a fee field: the problem message, or true when the value is fine. */
export const feeRule =
    (field: FeeField) =>
    (value: string): string | true =>
        validateFee(field, value) ?? true;
